// src/AuthContext.js

import React, { createContext, useState, useEffect } from 'react';
import axios from 'axios';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  axios.defaults.withCredentials = true;

  const refreshUser = async () => {
    if (!user?.id) {
      console.warn('⚠️ refreshUser: No user in context, cannot refresh');
      return null;
    }

    const apiUrl = process.env.REACT_APP_API_URL || 'https://koi-2028.onrender.com';

    try {
      console.log('🔄 refreshUser: Fetching user data for id:', user.id);

      const res = await axios.post(
        `${apiUrl}/get-user-by-id`,
        { user_id: user.id }
      );

      console.log('✅ refreshUser: Successfully fetched user data:', res.data);
      setUser(res.data);
      localStorage.setItem('user', JSON.stringify(res.data));
      return res.data;
    } catch (error) {
      console.error('❌ refreshUser: Failed to refresh user data:', error);
      // Return current user if refresh fails
      return user;
    }
  };

  // On mount, try to restore from localStorage AND then re-fetch the latest user details
  useEffect(() => {
    const init = async () => {
      const stored = localStorage.getItem('user');
      if (stored) {
        // Show whatever was in localStorage immediately
        const cachedUser = JSON.parse(stored);
        setUser(cachedUser);

        // Fetch fresh user data if we have a user_id
        if (cachedUser?.id) {
          const apiUrl = process.env.REACT_APP_API_URL || 'https://koi-2028.onrender.com';

          try {
            const res = await axios.post(
              `${apiUrl}/get-user-by-id`,
              { user_id: cachedUser.id }
            );
            setUser(res.data);
            localStorage.setItem('user', JSON.stringify(res.data));
            console.log('fetched user ➞', res.data);
          } catch (error) {
            // Keep using cached version if fetch fails
            console.log('Using cached user data');
          }
        }
      }
      setLoading(false);
    };

    init();
  }, []);

  // Standard email/password login
  const login = async (email, password) => {
    try {
      // 1) POST /login → get back a minimal user payload
      const apiUrl = process.env.REACT_APP_API_URL || 'https://koi-2028.onrender.com';
      const { data } = await axios.post(
        `${apiUrl}/login`,
        { email, password },
        { withCredentials: true }
      );

      // 2) Stick that into context & localStorage
      setUser(data.user);
      localStorage.setItem('user', JSON.stringify(data.user));

     // 3) Now fetch the full user details (which includes can_create_design)
     await refreshUser();

      return { success: true, user: data.user };
    } catch (err) {
      return {
        success: false,
        message: err.response?.data?.error || 'Login failed.'
      };
    }
  };

  // Google login flow
  const googleLogin = (googleUserData) => {
        setUser(googleUserData); // Update context
        localStorage.setItem('user', JSON.stringify(googleUserData)); // Persist user
    };

  // Logout clears context and localStorage
  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        googleLogin,
        logout,
        setUser,
         refreshUser
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
